import { useState } from "react";
import { InventoryDetailInterface } from "../../interfaces/InventoryInterface";
import { FormTextInput } from "../../components/FormFields";
import { CurrencyDollarIcon } from "@heroicons/react/24/outline";

interface InventoryDetailsTableProps {
  data?: InventoryDetailInterface[] | null;
  showItem?: boolean | true;
  showAllStatus?: boolean | false;
  showPiece?: boolean;
}

const InventoryDetailsTable: React.FC<InventoryDetailsTableProps> = ({
  data,
  showItem,
  showAllStatus,
  showPiece,
}) => {
  const [filterText, setFilterText] = useState("");

  const filteredData = data?.filter((row) => {
    const statusIsValid = showAllStatus || (row.statusID as number) !== 34; // Assuming 34 is invalid status

    return (
      statusIsValid &&
      (row.shipmentNumber.includes(filterText) ||
        row.serviceName?.includes(filterText) ||
        row.locationDestinationCode.includes(filterText))
    );
  });

  // Total de guías únicas
  const totalShipmentsUnique = filteredData
    ?.filter((x) => (x.statusID as number) !== 38 && (x.statusID as number) !== 34) // Excluyen faltantes=38 y eliminados=34
    .reduce((acumulador, objeto) => {
      acumulador[objeto.shipmentNumber] =
        (acumulador[objeto.shipmentNumber] || 0) + 1;
      return acumulador;
    }, {} as Record<string, number>);

  const totalShipments = Object.keys(totalShipmentsUnique || {}).length;

  
  // Total peso
  const sumTotalScaleWeight = filteredData
    ?.filter((x) => (x.statusID as number) !== 38 && (x.statusID as number) !== 34)
    .reduce((acumulador, objeto) => acumulador + objeto.physicalWeight, 0);

  // Total peso
  const sumTotalWeight = filteredData
    ?.filter((x) => (x.statusID as number) !== 38 && (x.statusID as number) !== 34)
    .reduce((acumulador, objeto) => acumulador + (objeto.chargedWeight || 0), 0);

  // Total de piezas
  const totalPiecesCount =
    filteredData?.filter((x) => (x.statusID as number) !== 38 && (x.statusID as number) !== 34)
      .length || 0;

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between my-4">
        <div className="mb-4 flex-1">
          <label className="block text-m font-medium leading-6 text-gray-800 mb-2 truncate">
            Filtrar
          </label>
          <FormTextInput
            type="text"
            placeholder="Por número de guía, origen, destino o servicio"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        <div className="mb-4 truncate">
          <label className="block text-m font-medium leading-6 text-gray-800 mb-2 truncate">
            Totales
          </label>
          <div className="border gap-6 bg-white rounded-xl shadow-md hover:shadow-lg flex flex-col justify-between sm:flex-row sm:items-center p-2">
            <div className="flex items-center w-full sm:w-1/3">
              <label className="block text-m leading-6 text-gray-800 truncate mb-1 sm:mb-0 mr-2">
                Guías:
              </label>
              <span className="text-gray-700 text-sm font-semibold sm:my-1">{totalShipments}</span>
            </div>
            <div className="flex items-center w-full sm:w-1/3">
              <label className="block text-m leading-6 text-gray-800 truncate mb-1 sm:mb-0 mr-2">
                Piezas:
              </label>
              <span className="text-gray-700 text-sm font-semibold sm:my-1">{totalPiecesCount}</span>
            </div>
            <div className="flex items-center w-full sm:w-1/3">
              <label className="block text-m leading-6 text-gray-800 truncate mb-1 sm:mb-0 mr-2">
                Peso balanza:
              </label>
              <span className="text-gray-700 text-sm font-semibold sm:my-1">{sumTotalScaleWeight?.toFixed(2)} kg</span>
            </div>
            <div className="flex items-center w-full sm:w-1/3">
              <label className="block text-m leading-6 text-gray-800 truncate mb-1 sm:mb-0 mr-2">
                Peso Facturado:
              </label>
              <span className="text-gray-700 text-sm font-semibold sm:my-1">{sumTotalWeight?.toFixed(2)} kg</span>
            </div>

            
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="table-auto w-full">
          <thead>
            <tr className="text-center">
              <th className="px-4 py-2 font-semibold text-xs">GUÍA</th>
              <th className="px-4 py-2 font-semibold text-xs">ORIGEN</th>
              <th className="px-4 py-2 font-semibold text-xs">DESTINO</th>
              <th className="px-4 py-2 font-semibold text-xs">SERVICIO / MODALIDAD DE PAGO</th>
              <th className="px-4 py-2 font-semibold text-xs">PIEZA</th>
              <th className="px-4 py-2 font-semibold text-xs">PESO</th>
            </tr>
          </thead>
          <tbody>
            {filteredData?.map((detail, index) => (
              <tr key={detail.inventoryDetailID} className={`cursor-pointer hover:bg-gray-100 text-center transition-opacity duration-500 
              ${detail.statusID === 34 ? "bg-red-200"
                    : detail.statusID === 38 // faltante
                    ? "bg-orange-200"
                    : index % 2 === 0
                    ? "bg-gray-50"
                    : "bg-white"}`}>
                <td className="px-4 py-2 text-xs">{detail.shipmentNumber}</td>
                <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                  <p>Tienda {detail.buCodeSource?.toUpperCase()}</p>
                  <p>{detail.locationOriginCode?.toUpperCase()}</p>
                </td>
                <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                  <p>Tienda {detail.buCodeDestination?.toUpperCase()}</p>
                  <p>{detail.locationDestinationCode?.toUpperCase()}</p>
                </td>
                <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                  <p>{detail.serviceName?.toUpperCase()}</p>
                  <p>{detail?.paymentModeName?.toUpperCase()}</p>
                </td>
                {showPiece && (
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                    <p>
                      {detail.pieceNumber} / {detail.totalPieces}
                    </p>
                    <p>
                      {detail.heigh} x {detail.width} x {detail.lenght} cm
                    </p>
                  </td>
                )}
                <td
                  className="px-4 py-4 text-xs text-left text-gray-700 font-semibold truncate"
                  title="Los totales de peso presentados corresponden a lo que se incluyó en el manifiesto"
                  data-te-toggle="tooltip"
                >
                  <div className="flex items-center">
                    <div className="w-3 mr-2">
                      {detail.physicalWeight === detail.chargedWeight &&
                        detail.physicalWeight > 0 && (
                          <CurrencyDollarIcon className="h-4 w-4 text-indigo-600" />
                        )}
                    </div>
                    Balanza: {detail.physicalWeight?.toFixed(2)} kg{" "}
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 mr-2">
                      {detail.dimensionalWeight === detail.chargedWeight &&
                        (detail.dimensionalWeight ?? 0) > 0 && (
                          <CurrencyDollarIcon className="h-4 w-4 text-indigo-600" />
                        )}
                    </div>
                    Volumétrico: {detail.dimensionalWeight?.toFixed(2)} kg{" "}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InventoryDetailsTable;